'use client';

import * as React from 'react';

import type { AriaButtonProps } from '@react-aria/button';
import type { ClassValue, VariantProps } from 'tailwind-variants';
import { tv } from 'tailwind-variants';

import { cn } from '@avodah-inc/ui/utils';

import { UnstyledButton } from '../unstyled-button';

const calendarDayVariants = tv({
  slots: {
    base: 'group flex flex-col items-center justify-center border-0 text-gray-900 group-hover:border-transparent',
    number:
      ' text-gray-900 data-[today=true]:text-base-white data-[active=true]:text-primary-500',
    text: 'text-gray-900 data-[active=true]:text-primary-500',
  },
  variants: {
    size: {
      sm: {
        base: 'h-8 w-8 min-w-fit min-h-fit rounded-lg hover:bg-primary-100 data-[today=true]:bg-primary-500 data-[today=true]:hover:bg-primary-600',
      },
      lg: {
        base: 'h-[3.125rem] w-[3.125rem] min-w-fit min-h-fit rounded data-[active=true]:text-primary-500 data-[active=true]:border data-[active=true]:border-primary-500 data-[today=false]:hover:bg-primary-100 data-[today=true]:hover:border-primary-600 ',
        number:
          'h-6 w-6 rounded -mt-[0.188rem] text-body-3 text-center data-[today=true]:bg-primary-500 data-[today=true]:group-hover:bg-primary-600 data-[today=true]:text-base-white',
        text: 'mb-[-3px] data-[today=true]:group-hover:text-primary-600 text-body-5',
      },
    },
  },
  defaultVariants: {
    size: 'lg',
  },
});

export type CalendarDayProps = AriaButtonProps &
  VariantProps<typeof calendarDayVariants> & {
    isActive?: boolean;
    isCurrentDay?: boolean;
    classNames?: {
      base?: ClassValue;
      text?: ClassValue;
      number?: ClassValue;
    };
  };

export const CalendarDay = React.forwardRef<
  HTMLButtonElement,
  CalendarDayProps
>(({ size, classNames, children, ...props }, forwardedRef) => {
  const { base, text, number } = calendarDayVariants({
    size,
  });

  return (
    <div className="relative">
      <UnstyledButton
        data-active={props.isActive}
        data-today={props.isCurrentDay}
        className={base({ class: classNames?.base })}
        ref={forwardedRef}
        {...props}
      >
        {React.Children.toArray(children).map((child, index) => (
          <div
            data-active={props.isActive}
            data-today={props.isCurrentDay}
            key={index}
            className={
              index === 0
                ? number({ class: classNames?.number })
                : text({ class: classNames?.text })
            }
          >
            {size === 'sm' && (
              <>
                <div
                  data-active={props.isActive}
                  data-today={props.isCurrentDay}
                  className={cn(
                    'absolute bottom-0 left-0 right-0 top-0 m-auto h-[1.875rem] w-[1.875rem] rounded-full border-white data-[active=true]:visible data-[active=false]:hidden data-[today=true]:border',
                    {},
                  )}
                />
                <div
                  data-active={props.isActive}
                  data-today={props.isCurrentDay}
                  className="border-primary-500 data-[today=true]:hover:border-primary-600 absolute bottom-0 left-0 right-0 top-0 m-auto h-8 w-8 rounded-full border data-[active=true]:visible data-[active=false]:hidden"
                />
              </>
            )}
            {child}
          </div>
        ))}
      </UnstyledButton>
    </div>
  );
});

CalendarDay.displayName = 'CalendarDay';
